/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2018
 */

/*global angular*/
import { SuccessService } from './../../services/success.service';
import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgModelDebounceChangeDirective } from '../../directives/ng-model-debounce.directive';
import { FormsModule } from '@angular/forms';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { catchError, of, tap } from 'rxjs';
import { Router } from '@angular/router';
import { PasswordResetService } from '../../services/password-reset.service';
import { environment } from '../../../environments/environment';
import { resolveTurnstileSiteKey } from '../../utils/turnstile-utils';

/**
 * The Turnstile script attaches a global. We only touch the handful of methods we use.
 */
declare const turnstile: {
  render: (el: HTMLElement, opts: any) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SCRIPT_ID = 'nexleader-turnstile-script';

@Component({
  selector: 'app-nexleader-password-reset',
  templateUrl: './password-reset.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, NgModelDebounceChangeDirective],
})
export class NexleaderPasswordResetComponent implements OnInit, AfterViewInit, OnDestroy {
  passwordReset: any = {};
  message: string | undefined;
  groups: any;
  readonly currentYear = new Date().getFullYear();

  /**
   * turnstileToken
   *
   * single-use token from the Turnstile widget, sent with the request that actually
   *  creates the reset and sends the email
   *
   * Note that update() deliberately does NOT send this - it posts noAction: true, which
   *  the server exempts from verification precisely so this token survives until submit.
   *
   * @type {string | null}
   */
  turnstileToken: string | null = null;

  private turnstileWidgetId: string | undefined;

  constructor(
    private router: Router,
    private passwordResetService: PasswordResetService,
    private errorHandler: ErrorHandlerService,
    private successService: SuccessService,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    if (this.errorHandler.error) {
      this.message = this.errorHandler.error.message;
    }
  }

  /**
   * ngAfterViewInit()
   *
   * Render the Turnstile widget once the container in our template exists. Loaded here
   *  rather than in index.html so the script isn't fetched on every route.
   */
  ngAfterViewInit() {
    this.loadTurnstileScript()
      .then(() => this.renderTurnstile())
      .catch(() => {
        // Cloudflare's CDN is unreachable (offline, corporate proxy, ad blocker). Leave
        //  the token null so submit stays disabled and the user gets an explanation
        //  instead of a confusing 403.
        this.errorHandler.handleError({
          message: 'Sorry, we could not load the human-verification widget. Please disable ' +
            'any ad blocker for this page and refresh, or contact support.',
          status: -1
        });
      });
  }

  ngOnDestroy() {
    if (this.turnstileWidgetId && typeof turnstile !== 'undefined') {
      try {
        turnstile.remove(this.turnstileWidgetId);
      } catch {
        // Already gone; nothing to do.
      }
    }
  }

  private loadTurnstileScript(): Promise<void> {
    if (typeof turnstile !== 'undefined') return Promise.resolve();

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      return new Promise<void>((resolve, reject) => {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject());
      });
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }

  /**
   * renderTurnstile()
   *
   * Explicit render (?render=explicit) rather than Cloudflare's automatic mode, which
   *  scans the DOM on script load and races Angular. Callbacks fire outside Angular's
   *  zone, so they go through NgZone.run or the submit button never re-enables.
   */
  private renderTurnstile() {
    const container = document.getElementById('nexleader-turnstile-container-password-reset');
    if (!container || this.turnstileWidgetId) return;

    this.turnstileWidgetId = turnstile.render(container, {
      sitekey: resolveTurnstileSiteKey(),
      callback: (token: string) => this.ngZone.run(() => {
        this.turnstileToken = token;
      }),
      'expired-callback': () => this.ngZone.run(() => {
        this.turnstileToken = null;
      }),
      'error-callback': () => this.ngZone.run(() => {
        this.turnstileToken = null;
      })
    });
  }

  /**
   * resetTurnstile()
   *
   * Discard the spent token and ask for a fresh one. Needed after any failed submit -
   *  including the 449 group-disambiguation response, which is a normal part of the flow
   *  for multi-group users and is always followed by a second submit.
   */
  private resetTurnstile() {
    this.turnstileToken = null;
    if (this.turnstileWidgetId && typeof turnstile !== 'undefined') {
      turnstile.reset(this.turnstileWidgetId);
    }
  }

  /**
   * isReadyToAdvance()
   *
   * @returns {boolean} true if we have an email and Turnstile has cleared the visitor
   */
  isReadyToAdvance(): boolean {
    if (!this.passwordReset.email) return false;
    if (!this.turnstileToken) return false;
    return true;
  }

  trackByGroupId(index: number, group: any): number {
    return group._id;
  }

  update() {
    const req = { noAction: true, ...this.passwordReset };
    this.passwordResetService
      .save(req)
      .pipe(
        tap((response) => {
          this.groups = null;
          delete this.passwordReset.group;
        }),
        catchError((error: any) => {
          if (error.status === 449) {
            this.groups = error.error.disambiguation;
          } else {
            this.groups = null;
          }
          return of(null);
        })
      )
      .subscribe();
  }

  sendPasswordReset() {
    if (!this.isReadyToAdvance()) return;

    this.passwordResetService
      .save({ ...this.passwordReset, turnstileToken: this.turnstileToken })
      .pipe(
        tap((_) => {
          this.successService.handle({ message: 'Password Reset Link Sent.' });
          this.router.navigate(['/']);
        }),
        catchError((error: any) => {
          // The token is spent either way. A 449 is especially important to reset for:
          //  it's the normal multi-group path and the user is about to submit again.
          this.resetTurnstile();
          if (error.status === 449) {
            this.groups = error.error.disambiguation;
          } else {
            this.errorHandler.handleError(
              error,
              this.errorHandler.types.TOAST
            );
          }
          return of(null);
        })
      )
      .subscribe();
  }
}

// angular.module('nexleader-ipsat').directive(
//   'ng17NexleaderPasswordResetComponent',
//   downgradeComponent({
//     component: NexleaderPasswordResetComponent,
//   }) as angular.IDirectiveFactory
// );
